import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../Utils/AxiosInstance";
import { revertAll } from "../ResetStateAction";

/* Initial State */
const initialState = {
  connected: false,
  email: "",
  accessToken: null,
  isAuthorized: false,
  message: null,
  status: "idle", // idle | loading | succeeded | failed
  error: null,
};

/* Get Google Calendar Status */
export const getGoogleCalendarStatus = createAsyncThunk(
  "GoogleCalendar/GetAccessToken",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/GoogleCalendar/GetAccessToken");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/* Authorize Google Calendar */
export const authorizeGoogleCalendar = createAsyncThunk("GoogleCalendar/Authorize", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get("/GoogleCalendar/Authorize");
    return response.data; // OAuth redirect URL
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
});

/* Disconnect Google Calendar */
export const disconnectGoogleCalendar = createAsyncThunk(
  "GoogleCalendar/Disconnect",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/GoogleCalendar/Disconnect");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/* Slice */
const GoogleCalendarSlice = createSlice({
  name: "GoogleCalendar",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(revertAll, () => initialState)

      /* GET STATUS */
      .addCase(getGoogleCalendarStatus.pending, state => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getGoogleCalendarStatus.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.connected = action.payload?.connected === true;
        state.email = action.payload?.email || "";
        state.accessToken = action.payload?.accessToken || null;
      })
      .addCase(getGoogleCalendarStatus.rejected, (state, action) => {
        state.status = "failed";
        state.connected = false;
        state.email = "";
        state.accessToken = null;
        state.error = action.payload;
      })

      /* AUTHORIZE */
      .addCase(authorizeGoogleCalendar.pending, state => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(authorizeGoogleCalendar.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isAuthorized = true;
        state.message = "Redirecting to Google for authorization...";
        state.redirectURL = action.payload;
      })
      .addCase(authorizeGoogleCalendar.rejected, (state, action) => {
        state.status = "failed";
        state.isAuthorized = false;
        state.error = action.payload || action.error.message;
      })

      /* DISCONNECT */
      .addCase(disconnectGoogleCalendar.pending, state => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(disconnectGoogleCalendar.fulfilled, state => {
        state.status = "succeeded";
        state.connected = false;
        state.email = "";
        state.accessToken = null;
        state.message = "Calendar disconnected successfully";
      })
      .addCase(disconnectGoogleCalendar.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      });
  },
});

/* Selectors */
export const getGoogleCalendarState = state => state.GoogleCalendar;

export default GoogleCalendarSlice.reducer;
